import React, { useState, useEffect } from 'react';
import { Percent, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/services/api';
import { toast } from 'sonner';

const Commissions = ({ user }) => {
  const [summary, setSummary] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryRes, commissionsRes] = await Promise.all([
        api.getCommissionsSummary(),
        api.getCommissions(),
      ]);
      setSummary(summaryRes.data);
      setCommissions(commissionsRes.data);
    } catch (error) {
      toast.error('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  };

  const handlePayCommission = async (commission, amount = null) => {
    try {
      const response = await api.payCommission(commission.id, amount);
      toast.success(response.data.message);
      setShowPaymentModal(false);
      setPaymentAmount('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pagar comisión');
    }
  };

  const openPaymentModal = (commission) => {
    setSelectedCommission(commission);
    const remaining = commission.amount - (commission.paid_amount || 0);
    setPaymentAmount(remaining.toString());
    setShowPaymentModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-[#C9A961] text-xl font-cormorant">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="commissions-page">
      <div className="mb-6">
        <h1 className="text-3xl font-cormorant font-bold text-[#2C2C2C]">Comisiones</h1>
        <p className="text-[#6B6B6B] mt-1">Seguimiento de comisiones del equipo</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {summary.map((staff) => (
          <Card
            key={staff.staff_id}
            className="p-6 border-[#C9A961]/20 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setSelectedStaff(staff.staff_id)}
            data-testid={`staff-summary-${staff.staff_id}`}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#C9A961] flex items-center justify-center">
                <Percent className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-[#2C2C2C]">{staff.staff_name}</h3>
                <p className="text-sm text-[#6B6B6B]">Total ganado</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B6B6B]">Pendiente:</span>
                <span className="font-semibold text-[#C9A961]">$ {staff.total_pending.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B6B6B]">Pagado:</span>
                <span className="font-semibold text-[#6B7C59]">$ {staff.total_paid.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[#C9A961]/20">
                <span className="font-medium text-[#2C2C2C]">Total:</span>
                <span className="text-lg font-cormorant font-bold text-[#2C2C2C]">
                  $ {staff.total_earned.toFixed(2)}
                </span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Commissions List */}
      <Card className="border-[#C9A961]/20">
        <div className="p-6 border-b border-[#C9A961]/20">
          <h2 className="text-xl font-cormorant font-semibold text-[#2C2C2C]">
            {selectedStaff
              ? `Comisiones de ${summary.find((s) => s.staff_id === selectedStaff)?.staff_name}`
              : 'Todas las Comisiones'}
          </h2>
          {selectedStaff && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedStaff(null)}
              className="mt-2"
            >
              Ver todas
            </Button>
          )}
        </div>

        <div className="divide-y divide-[#C9A961]/10">
          {commissions
            .filter((c) => !selectedStaff || c.staff_id === selectedStaff)
            .map((commission) => {
              const staffInfo = summary.find((s) => s.staff_id === commission.staff_id);
              return (
                <div
                  key={commission.id}
                  className="p-4 hover:bg-[#F5F2ED] transition-colors"
                  data-testid={`commission-${commission.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          commission.paid
                            ? 'bg-[#6B7C59]/20 text-[#6B7C59]'
                            : 'bg-[#C9A961]/20 text-[#C9A961]'
                        }`}
                      >
                        {commission.paid ? <CheckCircle size={20} /> : <Clock size={20} />}
                      </div>
                      <div>
                        <p className="font-medium text-[#2C2C2C]">{staffInfo?.staff_name || 'Staff'}</p>
                        <p className="text-sm text-[#6B6B6B]">
                          Reserva: {commission.booking_id?.slice(0, 8)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-lg font-semibold text-[#C9A961]">
                          $ {commission.amount.toFixed(2)}
                        </p>
                        <p className="text-sm text-[#6B6B6B]">
                          {commission.paid ? 'Pagado' : 'Pendiente'}
                        </p>
                      </div>
                      {!commission.paid && user.role === 'admin' && (
                        <Button
                          size="sm"
                          onClick={() => handlePayCommission(commission.id)}
                          data-testid={`pay-commission-${commission.id}`}
                          className="bg-[#6B7C59] hover:bg-[#5A6B49]"
                        >
                          Pagar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </Card>
    </div>
  );
};

export default Commissions;